import * as React from "react";

import { default as DataGrid } from "../../../components/LocalizedDataGrid";
import { GridToolbarContainer, GridActionsCellItem } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import Box from "@mui/material/Box";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import GeneratorEditPopUp from "./GeneratorEditPopUp";
import Button from "@mui/material/Button";

import { useTranslation } from "react-i18next";
import { ApiContext } from "../../../App";

const defaultData = {
	identifier: "iden",
	capacities_MWh: "500,1000",
	fuel_consumption: "100,100",
	cold_start_penalty_h: "1.5",
	extra_cost_per_MWh: "10",
	extra_cost_per_h: "15",
	block_size: "0.5",
	in_operation: true,
	facility: null,
};

export default function GeneratorEdit(props) {
	const { facility } = props;

	const { t } = useTranslation();

	const [rows, setRows] = React.useState([]);

	const [editPopUpOpen, setEditPopUpOpen] = React.useState(false);
	const [editPopUpData, setEditPopUpData] = React.useState(defaultData);
	const {generatorApi} = React.useContext(ApiContext)

	React.useEffect(() => {
		generatorApi.list(
			facility.split("/").slice(-2)[0],
			(data) => {
				let data_with_id = data.map((dict) => {
					let with_id_url = { ...dict, id: dict.url };
					delete with_id_url.url;
					return with_id_url;
				});

				setRows(data_with_id);
			},
			() => {
				alert(t("Couldn't connect to server."));
			}
		);
	}, [facility]);

	const handleEditClick = (id) => {
		setEditPopUpOpen(true);
		setEditPopUpData(rows.find((item) => item.id === id));
	};

	const handleDuplicateClick = (id) => {
		const from_data = rows.filter((row) => row.id === id)[0];
		let to_data = { ...from_data };
		delete to_data.id;

		setEditPopUpData(to_data);
		setEditPopUpOpen(true);
	};

	const handleDeleteClick = (id) => {
		const confirmed = window.confirm(t("Delete this generator?"));

		if (confirmed) {
			generatorApi.delete(
				id,
				() => {
					setRows(rows.filter((row) => row.id !== id));
				},
				() => {
					alert(t("The generator couldn't be deleted."));
				}
			);
		}
	};

	const handleEditPopUpClosedWithoutSaving = () => {
		setEditPopUpOpen(false);
	};

	const handleEditPopUpClosedWithUpdate = (new_data) => {
		if (rows.find((item) => item.id === new_data.id))
			setRows(rows.map((row) => (row.id === new_data.id ? new_data : row)));
		else setRows((oldRows) => [...oldRows, new_data]);

		setEditPopUpOpen(false);
	};

	const handleAddGenerator = () => {
		setEditPopUpData({ ...defaultData, facility: facility });
		setEditPopUpOpen(true);
	};

	const columns = [
		{
			field: "identifier",
			headerName: t("ID"),
			minWidth: 100,
		},
		{
			field: "name",
			headerName: t("Device"),
			flex: 1,
			minWidth: 100,
		},
		{
			field: "location",
			headerName: t("Location"),
			flex: 1,
			minWidth: 100,
		},
		{
			field: "capacities_MWh",
			headerName: t("Capacities"),
			flex: 1,
			minWidth: 100,
			valueFormatter: (params) =>
				params.value
					.split(",")
					.map((elem) => elem + " MW")
					.join(", "),
		},
		{
			field: "in_operation",
			headerName: t("In operation"),
			minWidth: 50,
			type: "boolean",
		},
		{
			field: "actions",
			type: "actions",
			headerName: t("Actions"),
			flex: 0.5,
			minWidth: 150,
			cellClassName: "actions",
			getActions: ({ id }) => {
				return [
					<GridActionsCellItem
						icon={<EditIcon />}
						label={t("Edit")}
						className="textPrimary"
						onClick={() => handleEditClick(id)}
						color="inherit"
					/>,
					<GridActionsCellItem
						icon={<ContentCopyIcon />}
						label={t("Duplicate")}
						onClick={() => handleDuplicateClick(id)}
						color="inherit"
					/>,
					<GridActionsCellItem
						icon={<DeleteIcon />}
						label={t("Delete")}
						onClick={() => handleDeleteClick(id)}
						color="inherit"
					/>,
				];
			},
		},
	];

	const gridToolbar = (props) => {
		return (
			<GridToolbarContainer>
				<Button
					color="primary"
					size="small"
					startIcon={<AddIcon />}
					onClick={() => props.onClick()}
				>
					{t("Add generator")}
				</Button>
			</GridToolbarContainer>
		);
	};

	return (
		<>
			<Box height={"100%"}>
				<DataGrid
					rows={rows}
					columns={columns}
					components={{
						Toolbar: gridToolbar,
					}}
					componentsProps={{
						toolbar: { onClick: handleAddGenerator },
					}}
				/>
			</Box>
			<GeneratorEditPopUp
				open={editPopUpOpen}
				data={editPopUpData}
				onCloseWithoutSaving={handleEditPopUpClosedWithoutSaving}
				onCloseWithUpdate={handleEditPopUpClosedWithUpdate}
			/>
		</>
	);
}
